import React, { useState, useEffect } from "react";
import { fetchContent, adminUpdateContent, adminResetContent } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Loader2, Save, RotateCcw, Car as CarIcon, Bike, Clock } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm";
const labelCls = "text-xs font-semibold uppercase tracking-wide text-gray-500 block mb-1";

const CategoryBlock = ({ title, icon: Icon, data, onChange }) => (
  <div className="bg-white border border-black/10 p-6 mb-6">
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} className="gpm-text-green" />
      <h3 className="font-serif-display text-lg font-bold gpm-text-green">{title}</h3>
    </div>
    <label className="flex items-center gap-3 text-sm mb-4">
      <input type="checkbox" data-testid={`cs-enabled-${title.toLowerCase()}`} checked={data.enabled !== false} onChange={(e) => onChange({ ...data, enabled: e.target.checked })} />
      Show this section on the {title} page
    </label>
    <div className="space-y-4">
      <div><label className={labelCls}>Heading</label><input data-testid={`cs-title-${title.toLowerCase()}`} className={inputCls} value={data.title || ""} onChange={(e) => onChange({ ...data, title: e.target.value })} /></div>
      <div><label className={labelCls}>Intro line</label><textarea data-testid={`cs-intro-${title.toLowerCase()}`} rows={2} className={inputCls} value={data.intro || ""} onChange={(e) => onChange({ ...data, intro: e.target.value })} /></div>
      <div>
        <label className={labelCls}>Vehicles (one per line)</label>
        <textarea data-testid={`cs-list-${title.toLowerCase()}`} rows={6} className={inputCls} placeholder={"1972 Jaguar E-Type — arriving soon\n1965 Porsche 911 — due next month"} value={data.list || ""} onChange={(e) => onChange({ ...data, list: e.target.value })} />
        <p className="text-xs text-gray-400 mt-1">Type each vehicle on its own line. They'll appear as a bulleted list.</p>
      </div>
    </div>
  </div>
);

const ComingSoonEditor = () => {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = () => fetchContent("comingSoon").then(setC).catch(() => setC(null));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { cars: c.cars, motorcycles: c.motorcycles };
      await adminUpdateContent("comingSoon", payload);
      toast({ title: "Saved", description: "Your Coming Soon lists are updated." });
    } catch {
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset both Coming Soon lists to defaults?")) return;
    setC(await adminResetContent("comingSoon"));
    toast({ title: "Reset to default" });
  };

  if (!c) return <div className="py-20 text-center text-gray-500" data-testid="coming-soon-loading">Loading…</div>;

  return (
    <div className="max-w-3xl" data-testid="coming-soon-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif-display text-2xl font-bold gpm-text-green flex items-center gap-2"><Clock size={20} /> Coming Soon</h2>
          <p className="text-sm text-gray-500">List vehicles arriving soon — shown at the bottom of your Cars and Motorcycles pages.</p>
        </div>
        <div className="flex gap-3">
          <button data-testid="cs-reset" onClick={reset} className="flex items-center gap-2 px-4 py-2.5 border border-black/20 text-sm font-semibold"><RotateCcw size={15} /> Reset</button>
          <button data-testid="cs-save" onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes</button>
        </div>
      </div>

      <CategoryBlock title="Cars" icon={CarIcon} data={c.cars || {}} onChange={(v) => setC({ ...c, cars: v })} />
      <CategoryBlock title="Motorcycles" icon={Bike} data={c.motorcycles || {}} onChange={(v) => setC({ ...c, motorcycles: v })} />
    </div>
  );
};

export default ComingSoonEditor;
